/** Pure economy math (PRD §7). All numbers come from economy.json via callers. */

/** XP per round (MT-05.1): win 30, fail 10, 3 stars +10. */
export function xpForRound(won: boolean, stars: number, cfg = { win: 30, fail: 10, threeStarBonus: 10 }): number {
  return (won ? cfg.win : cfg.fail) + (won && stars === 3 ? cfg.threeStarBonus : 0);
}

/**
 * `table[n]` = cumulative XP needed to reach player level n+1 (table[0] = 0).
 * Precomputed from 100 × n^1.4 at build time (tools) so runtimes agree exactly.
 */
export function levelForXp(table: readonly number[], xp: number): number {
  let lvl = 1;
  for (let i = 1; i < table.length; i++) {
    if (xp >= table[i]!) lvl = i + 1;
    else break;
  }
  return lvl;
}

export function xpProgress(table: readonly number[], xp: number): { level: number; into: number; span: number } {
  const level = levelForXp(table, xp);
  if (level >= table.length) return { level, into: 1, span: 1 }; // max level: full bar
  const lo = table[level - 1] ?? 0;
  const hi = table[level]!;
  return { level, into: xp - lo, span: Math.max(1, hi - lo) };
}

/** MT-03.1: shop rate rises 5% per upgrade. Coins per hour. */
export function shopRate(baseRatePerHour: number, upgrades: number, pctPerUpgrade = 5): number {
  return Math.floor((baseRatePerHour * (100 + pctPerUpgrade * upgrades)) / 100);
}

/** MT-03.1: offline sales = rate × hours away, capped (default 8h). */
export function offlineSales(ratePerHour: number, awayMs: number, capHours = 8): number {
  const ms = Math.max(0, Math.min(awayMs, capHours * 3600000));
  return Math.floor((ratePerHour * ms) / 3600000);
}

/** GP-08.1: combo multiplier in basis points, +10% per step, capped at ×2. */
export function comboMultiplierBps(step: number): number {
  return Math.min(10000 + 1000 * Math.max(0, step), 20000);
}

export interface UpgradeCost {
  coins: number;
  stars: number;
}

/** MT-02.2: remaining coins/stars needed for an upgrade (never a paywall). */
export function upgradeShortfall(cost: UpgradeCost, have: { coins: number; stars: number }): UpgradeCost {
  return { coins: Math.max(0, cost.coins - have.coins), stars: Math.max(0, cost.stars - have.stars) };
}
